import {SECTION_DURATION_MS} from '../section/sectionApi';
import {computeSectionInstant} from '../section/slotTime';
import {ONE_MINUTE_MS, ONE_WEEK_MS} from '../utils/time';

/**
 * How long a student has to answer a TeachNow offer. The student's countdown,
 * the teacher's countdown and the server-side timeout all use this.
 */
export const TEACHNOW_OFFER_WINDOW_MS = 45 * 1000;

/**
 * How often a student's IDE refreshes their presence doc while they are
 * working, in courses with the `teachnow` feature.
 */
export const TEACHNOW_PRESENCE_HEARTBEAT_MS = 60 * 1000;

/**
 * How recently a student must have edited or run code, by their presence
 * doc, to be offered help. Spans two missed heartbeats.
 */
export const TEACHNOW_ENGAGED_WINDOW_MS = 2.5 * 60 * 1000;

/**
 * How long before a section starts, and after it ends, TeachNow leaves its
 * students alone.
 */
export const TEACHNOW_SECTION_BUFFER_MS = 10 * ONE_MINUTE_MS;

/**
 * Fields the IDE writes to `presence/{courseId}/users/{uid}` for TeachNow
 * matching. Every field is optional: older clients write none of them, and
 * the matcher treats a missing field as neutral.
 */
export interface TeachNowPresenceSignals {
  /** Runs (and test runs) since the student opened this assignment. */
  runsOnAssn?: number;
  /** Of those, runs that ended in an error or a failing test. */
  failedRunsOnAssn?: number;
  /** Whether the latest run failed. */
  lastRunFailed?: boolean;
  /** Last edit or run, as epoch ms of the server clock. */
  lastActivityMs?: number;
  /** When the tab was hidden, as epoch ms; `null` while it is visible. */
  hiddenSinceMs?: number | null;
}

/**
 * When a section first meets, from its doc and the course anchor: a super
 * section's own `superSectionFirstTimestamp`, else the course
 * `firstSectionTimestamp` plus the section's `timeIndex` hours.
 *
 * @param args.section - The section doc's timing fields.
 * @param args.firstSectionTimestamp - The course's ISO 8601 anchor, if set.
 * @return Epoch ms, or `null` when the section has no meeting time.
 * @throws Never; unparseable timestamps yield `null`.
 */
export function getSectionFirstMeetingMs({
  section,
  firstSectionTimestamp,
}: {
  section: {
    timeIndex?: number | string | null;
    isSuperSection?: boolean;
    superSectionFirstTimestamp?: string | null;
  };
  firstSectionTimestamp?: string | null;
}): number | null {
  if (section.isSuperSection === true) {
    const superMs = Date.parse(section.superSectionFirstTimestamp ?? '');
    return Number.isNaN(superMs) ? null : superMs;
  }
  if (typeof section.timeIndex !== 'number' || !firstSectionTimestamp) {
    return null;
  }
  if (Number.isNaN(Date.parse(firstSectionTimestamp))) {
    return null;
  }
  return computeSectionInstant({
    firstSectionTimestamp,
    timeIndex: section.timeIndex,
    weekIndex: 0,
  }).getTime();
}

/**
 * Finds the weekly section meeting whose TeachNow quiet window contains
 * `nowMs`. The window is the closed interval
 * `[start - bufferMs, end + bufferMs]`; both boundaries are inclusive.
 *
 * Meetings repeat weekly from `firstMeetingMs` and last
 * {@link SECTION_DURATION_MS}. Times before the first meeting's window are
 * outside it.
 *
 * @param args.firstMeetingMs - Start of the first meeting, epoch ms (see
 *   {@link getSectionFirstMeetingMs}).
 * @param args.nowMs - Epoch ms to check.
 * @param args.bufferMs - Quiet time before the start and after the end.
 *   Defaults to {@link TEACHNOW_SECTION_BUFFER_MS}.
 * @return The meeting whose window contains `nowMs`, or `null` (also when
 *   either time is not a finite number).
 * @throws Never.
 */
export function findSectionQuietWindow({
  firstMeetingMs,
  nowMs,
  bufferMs = TEACHNOW_SECTION_BUFFER_MS,
}: {
  firstMeetingMs: number;
  nowMs: number;
  bufferMs?: number;
}): {startMs: number; endMs: number} | null {
  if (!Number.isFinite(firstMeetingMs) || !Number.isFinite(nowMs)) {
    return null;
  }
  const meetingMs = SECTION_DURATION_MS;
  const weekIndex = Math.floor(
    (nowMs - firstMeetingMs + bufferMs) / ONE_WEEK_MS,
  );
  if (weekIndex < 0) {
    return null;
  }
  const startMs = firstMeetingMs + weekIndex * ONE_WEEK_MS;
  if (nowMs < startMs - bufferMs || nowMs > startMs + meetingMs + bufferMs) {
    return null;
  }
  return {startMs, endMs: startMs + meetingMs};
}
